import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from '@carta/fep-analytics';

type UseInkAnalyticsOptions = {
  ref?: React.RefObject<any>;
};

const dataTrackKeys: string[] = ['data-track-name', 'data-track-events', 'data-track-payload'];

/* 
 This hook was created based on the `useAnalytics` hook from `@carte/fep-analytics.
 This hook is for internal usage only inside Ink interactive Components, it ties the usage 
 of the `useAnalytics` hook, so folks can use it and have a better API and implementation
 for Ink Components in particular.

 It's worth noting that, this is NOT a replacement fo the `useAnalytics` hook, but an extension.
 You can use both `trackEvent` AND `data-track-*` attributes at the same time in your application. 
 They are not exclusive implementations.
*/
function useInkAnalytics(props: any, options: UseInkAnalyticsOptions = {}) {
  // Breaks down `data-track-*` attributes to they are more easely manipulated
  const dataTracks: any = Object.keys(props)
    .filter(key => dataTrackKeys.includes(key))
    .reduce((obj, key) => {
      const ret = obj;
      ret[key.split('-')[2]] = props[key];
      return ret;
    }, {});

  // If the Component receives an external ref, we should account for that when initializing the internal ref
  let ref: React.RefObject<any> = useRef();
  ref = options.ref || ref;

  // Ink also utilizes the `trackEvent` function from `fep-analytics`
  const { payload = {} } = dataTracks;
  const { trackEvent } = useAnalytics(payload);

  // This will call the `dispatch` function that is defined in the `useAnalytics` call.
  // We can extend this function to add more information to the `dispatch` callback.
  const analyticsCall = useCallback(
    e =>
      trackEvent({
        name: dataTracks.name,
        action: `${e.type} ${e.target.localName}`,
        ...payload,
      }),
    [dataTracks.name, payload, trackEvent],
  );

  // By using `addEventListener`s calls, we add the proper event as defined in the `data-track-*` attributes to the ref.
  // We then remove those on unmount.
  useEffect(() => {
    const current = ref.current;

    if (dataTracks.name) {
      dataTracks.events.forEach((event: any) => {
        current.addEventListener(event, analyticsCall);
      });

      return () => {
        dataTracks.events.forEach((event: any) => {
          current.removeEventListener(event, analyticsCall);
        });
      };
    }
    return undefined;
  }, [analyticsCall, dataTracks, ref]);

  return [ref];
}

export default useInkAnalytics;
